import React, { type FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import type { RootState } from 'bootstrap';
import { Button, Dialog } from 'shamrock-clover-ui';
import rtsproUserService from '../../services/rtsproUser.service';
import { redirectToLogout, redirectToV2Logout } from '../../utils/utils';
import termsClipboard from '../../assets/images/termsClipboard.svg';
import css from './styles/termsAndConditionsModal.scss';
import { useAuth0 } from '@auth0/auth0-react';

interface ActionButtonsProps {
  handleAccept: () => void;
}

const ActionButtons: FC<ActionButtonsProps> = ({ handleAccept }) => {
  const { t } = useTranslation();
  const {isAuthenticated: isAuthenticatedAuth0} = useAuth0();
  return (
    <div className={css.buttons}>
      <Button buttonStyle='outlined' onClick={isAuthenticatedAuth0 ? redirectToV2Logout : redirectToLogout} data-testid="decline-terms-button">{t('termsAndConditionsModal.decline')}</Button>
      <Button onClick={handleAccept} data-testid="accept-terms-button">{t('termsAndConditionsModal.accept')}</Button>
    </div>
  );
};

const Content: FC = () => {
  const { t } = useTranslation();

  return (
    <div className={css.content}>
      <div>
        <img src={termsClipboard} alt={t('termsAndConditionsModal.imageAlt')} />
      </div>
      <div className={css.contentHeader}>{t('termsAndConditionsModal.header')}</div>
      <div className={css.contentBody} data-testid="terms-body">
        <Trans
          i18nKey="termsAndConditionsModal.body"
          components={{
            privacyLink: <a href="https://www.rtsinc.com/page/privacy-policy" target="_blank" />,
            eulaLink: <a href="https://www.rtsinc.com/page/mobile-user-agreement" target="_blank" />,
            br: <br />,
          }}
        />
      </div>
    </div>
  );
};

export const TermsAndConditionsModal: FC = () => {
  const [email, isTrialUser, acceptedLatestTos] = useSelector<RootState, [string, boolean, string[]]>((state) => ([
    state.app.user.email,
    state.app.user.userStatus?.isTrial ?? false,
    state.app.user.acceptedLatestTos ?? [],
  ]));
  const termsOfUseAccepted = acceptedLatestTos.includes('terms-of-use');
  const [isOpen, setIsOpen] = useState(!termsOfUseAccepted && !isTrialUser);

  useEffect(() => {
    if (!termsOfUseAccepted && !isTrialUser) setIsOpen(true);
  }, [termsOfUseAccepted, isTrialUser]);

  const handleAccept = async () => {
    setIsOpen(false);
    try {
      await rtsproUserService.acceptTermsAndConditions(
        email,
        {
          acceptedFrom: 'rtspro-core-modal',
          termsType: 'terms-of-use'
        }
      );
    } catch (e) {}
  };

  return (
    <div className={css.dialogOverrides}>
      <Dialog
        open={isOpen}
        actions={<ActionButtons handleAccept={handleAccept} />}
        content={<Content />}
        width='450px'
      />
    </div>
  );
};

export default TermsAndConditionsModal;
